import React, {
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'

interface Props<RowType> {
  rows: RowType[]
  onSelection?: (rows: RowType[]) => void
  onGlobalSelection?: (rows: RowType[]) => void
  idField: keyof RowType
  headerCellStyles?: any
  cellStyles?: any
  onRowClick?: (row: any) => void
  loading?: boolean
  showRowSelected?: (row: RowType) => boolean
  showRowDisabled?: (row: RowType) => boolean
}

const CELL_PADDING = '8px 10px'

const useTableLogic = <Row>({
  rows,
  onSelection,
  idField,
  headerCellStyles,
  cellStyles,
  onRowClick,
  loading,
  showRowSelected,
  showRowDisabled
}: Props<Row>) => {
  const [selectedRows, setSelectedRows] = useState<Row[]>([])
  const [globalSelectedRows, setGlobalSelectedRows] = useState<Row[]>([])
  const [expandedRows, setExpandedRows] = useState<Row[]>([])
  const [rowClicked, setRowClicked] = useState<Row | null>(null)

  useEffect(() => {
    if (loading) {
      setSelectedRows([])
      setExpandedRows([])
      setRowClicked(null)
    }
  }, [loading])

  useEffect(() => {
    onSelection?.(globalSelectedRows)
  }, [onSelection, globalSelectedRows])

  const handleCheckAll = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const notAlreadySelectedRows = showRowSelected
        ? rows.filter((row) => !showRowSelected(row))
        : rows
      if (e.target.checked) {
        setSelectedRows(notAlreadySelectedRows)
        const newGlobalSelected = [...globalSelectedRows]
        notAlreadySelectedRows.forEach((row) => {
          const exists = newGlobalSelected.find(
            (r) => r[idField] === row[idField]
          )
          if (!exists && !showRowDisabled?.(row)) newGlobalSelected.push(row)
        })
        setGlobalSelectedRows(newGlobalSelected)
      } else {
        setSelectedRows([])
        const newGlobalSelected = globalSelectedRows.filter(
          (globalRow) =>
            !notAlreadySelectedRows.some(
              (row) => row[idField] === globalRow[idField]
            )
        )
        setGlobalSelectedRows(newGlobalSelected)
      }
    },
    [showRowSelected, rows, globalSelectedRows, idField]
  )

  const allRowsChecked = useMemo(() => {
    const notAlreadySelectedRows = showRowSelected
      ? rows.filter((row) => !showRowSelected(row))
      : rows
    return (
      rows.length > 0 && selectedRows.length === notAlreadySelectedRows.length
    )
  }, [showRowSelected, rows, selectedRows.length])

  const handleCheck = useCallback(
    (
      selectedRows: Row[],
      setSelectedRows: (_: SetStateAction<Row[]> | Row[]) => void
    ) =>
      (e: React.ChangeEvent<HTMLInputElement>, selectedRow: any) => {
        const existRow = !!selectedRows.find(
          (row: any) => row[idField] === selectedRow[idField]
        )
        if (e.target.checked && !existRow)
          setSelectedRows((old) => [...old, selectedRow])
        else if (!e.target.checked && existRow) {
          const withoutSelectedRow = selectedRows.filter(
            (row: any) => row[idField] != selectedRow[idField]
          )
          setSelectedRows(withoutSelectedRow)
        }
      },
    [idField]
  )

  const handleRowCheck = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, selectedRow: any) => {
      const existInLocal = !!selectedRows.find(
        (row: any) => row[idField] === selectedRow[idField]
      )
      const existInGlobal = !!globalSelectedRows.find(
        (row: any) => row[idField] === selectedRow[idField]
      )

      if (e.target.checked) {
        if (!existInLocal) setSelectedRows((old) => [...old, selectedRow])
        if (!existInGlobal)
          setGlobalSelectedRows((old) => [...old, selectedRow])
      } else {
        if (existInLocal) {
          setSelectedRows((prev) =>
            prev.filter((row: any) => row[idField] !== selectedRow[idField])
          )
        }
        if (existInGlobal) {
          setGlobalSelectedRows((prev) =>
            prev.filter((row: any) => row[idField] !== selectedRow[idField])
          )
        }
      }
    },
    [selectedRows, globalSelectedRows, idField]
  )

  const handleRowClick = (row: Row) => {
    setRowClicked(row)
    onRowClick?.(row)
  }

  const isRowActive = useCallback(
    (rowList: any[]) => (selectedRow: any) =>
      !!rowList.find((row: any) => row[idField] === selectedRow[idField]),
    [idField]
  )

  const isRowSelected = useCallback(
    (selectedRow: any) => {
      return !!globalSelectedRows.find(
        (row: any) => row[idField] === selectedRow[idField]
      )
    },
    [globalSelectedRows, idField]
  )

  const isRowDisabled = useCallback(
    (selectedRow: any) => {
      return showRowDisabled?.(selectedRow)
    },
    [globalSelectedRows, idField]
  )

  useEffect(() => {
    const currentPageSelected = rows.filter((row) =>
      globalSelectedRows.some(
        (globalRow) => globalRow[idField] === row[idField]
      )
    )
    setSelectedRows(currentPageSelected)
  }, [rows, globalSelectedRows, idField])

  const headerCellStyle = useMemo(
    () =>
      headerCellStyles || {
        padding: CELL_PADDING,
        color: '#878A99',
        backgroundColor: '#F3F6F9'
      },
    [headerCellStyles]
  )

  const cellStyle = useMemo(
    () =>
      cellStyles || {
        padding: CELL_PADDING
      },
    [cellStyles]
  )

  return {
    handleCheckAll,
    allRowsChecked,
    handleRowCheck,
    isRowSelected,
    isRowDisabled,
    handleRowExpansion: handleCheck(expandedRows, setExpandedRows),
    isRowExpanded: (row: any) => isRowActive(expandedRows)(row),
    rowClicked,
    handleRowClick,
    headerCellStyle,
    cellStyle,
    CELL_PADDING
  }
}

export default useTableLogic
